"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createArgusTheme = exports.createArgusThemeFactory = void 0;
const locale_1 = require("@mui/material/locale");
const locales_1 = require("@mui/x-date-pickers/locales");
const pure_utils_1 = require("@mnemonic/pure-utils");
const locale_2 = require("../locale");
const locale_3 = require("../locale");
const styles_1 = require("../styles");
const getThemeModeFromAdapter = (adapter) => {
    const prefValue = String(adapter?.user?.properties?.["ui.themeMode"]);
    const validThemeModes = ["light", "dark", "auto"];
    const idx = validThemeModes.findIndex((mode) => mode === prefValue);
    // If we cannot look up the prefValue in the list of valid modes then we return 'light' as default.
    return idx > -1 ? validThemeModes[idx] : "auto";
};
const getLocaleFromAdapter = (adapter) => {
    const language = adapter?.user?.language;
    if (/^[a-z]{2}-[a-z]{2}$/i.test(language)) {
        return language;
    }
    else if (language === "NORWEGIAN") {
        return "nb-NO";
    }
    else if (language === "ENGLISH") {
        return "en-US";
    }
    console.warn(`No available local translation for language ${language}. Returning 'en-US'`);
    return "en-US";
};
const createArgusThemeFactory = (arg) => (adapter, cb) => {
    const { matchMedia, location } = arg;
    const { metaInfo } = (0, pure_utils_1.getTargetMeta)(location);
    const { matches } = matchMedia("(prefers-color-scheme: dark)");
    /**
     * SystemSettings here represents data which is defined
     * by the browser/user outside of argus
     **/
    const SystemSettings = {
        /* defaultTheme based on the current domain name */
        defaultTheme: metaInfo.theme,
        /* ColorScheme configured through the browser / operating system */
        colorScheme: matches ? "dark" : "light",
        /* The locale reported by the browser */
        locale: navigator?.language ||
            //@ts-ignore lib.dom.ts doesn't recognize navigator.userLanguage
            navigator?.userLanguage,
        /* The default TimeZone reported by the browser */
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const UserSettings = {
        isDarkModeAllowed: Boolean(adapter?.functions?.find(({ name }) => name === "allowDarkMode")) || false,
        userThemeMode: getThemeModeFromAdapter(adapter),
        locale: getLocaleFromAdapter(adapter),
    };
    let createTheme = null;
    // Check if the default theme is 'nfcert', which does not support dark mode
    if (SystemSettings.defaultTheme === "nfcert") {
        createTheme = styles_1.createNfcertTheme;
    }
    else if (
    // Check if the user is not allowed to use dark mode, or
    // the user has explicitly requested the light theme, or
    // the user wants the theme to follow the system settings and the system is set to light mode
    !UserSettings.isDarkModeAllowed ||
        UserSettings.userThemeMode === "light" ||
        (UserSettings.userThemeMode === "auto" && SystemSettings.colorScheme === "light")) {
        createTheme = styles_1.createMnemonicLightTheme;
    }
    else if (
    // Check if the user is allowed to use dark mode, and
    // the user has explicitly requested the dark theme, or
    // the user wants the theme to follow the system settings and the system is set to dark mode
    UserSettings.isDarkModeAllowed &&
        (UserSettings.userThemeMode === "dark" ||
            (UserSettings.userThemeMode === "auto" && SystemSettings.colorScheme === "dark"))) {
        createTheme = styles_1.createMnemonicDarkTheme;
    }
    else {
        // Default to the light theme if no other conditions are met
        createTheme = styles_1.createMnemonicLightTheme;
    }
    let localizationOpts = null;
    if (UserSettings.locale === "nb-NO") {
        localizationOpts = [locale_1.nbNO, locales_1.nbNO, locale_2.nbNO];
    }
    else if (UserSettings.locale === "en-US") {
        localizationOpts = [locale_1.enUS, locales_1.enUS, locale_3.enUS];
    }
    else {
        console.warn(`No locale settings provided`);
        localizationOpts = [locale_1.enUS, locales_1.enUS, locale_3.enUS];
    }
    return cb(createTheme, localizationOpts, { SystemSettings, UserSettings });
};
exports.createArgusThemeFactory = createArgusThemeFactory;
exports.createArgusTheme = (0, exports.createArgusThemeFactory)({ matchMedia: window.matchMedia, location: window.location });
